import React from "react";
import { createTheme } from "@mui/material/styles";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";

// Fonts loaded via Google Fonts CDN in gatsby-ssr.tsx

// Create rtl cache with optimizations
// Use "css" key so Gatsby's emotion plugin can extract styles during SSR
// For browser, we need to use the insertion point to prevent FOUC
let cacheRtl: ReturnType<typeof createCache> | null = null;

function getOrCreateCache() {
  // In SSR, create a fresh cache per render
  // Using "css" key ensures Gatsby's emotion plugin extracts styles
  if (typeof document === "undefined") {
    // SSR: create fresh cache with "css" key for Gatsby extraction
    return createCache({
      key: "css", // Use default key so Gatsby's plugin extracts styles
      stylisPlugins: [rtlPlugin],
      prepend: false, // Don't prepend in SSR - Gatsby extracts styles
    });
  }

  // Browser: reuse cache with insertion point to prevent FOUC
  if (!cacheRtl) {
    const insertionPointElement = document.querySelector<HTMLElement>(
      'meta[name="emotion-insertion-point"]'
    );

    cacheRtl = createCache({
      key: "css", // Use same key for consistency
      stylisPlugins: [rtlPlugin],
      prepend: true, // Prepend in browser to inject before other styles
      insertionPoint: insertionPointElement || undefined,
    });
  }

  return cacheRtl;
}

export { getOrCreateCache as cacheRtl };

// RTL wrapper component
export const RTL: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cache = getOrCreateCache();
  return React.createElement(CacheProvider, { value: cache }, children);
};

// Custom gradient colors
const gradientColors = {
  primary:
    "radial-gradient(circle at 93% 16%, #F3E3EE 0%, #CEE9F4 25%, #D1EBDD 60%, #F3F8F0 100%)",
  primaryHover:
    "radial-gradient(circle at 93% 16%, #E8D1E0 0%, #B8D9E8 25%, #C1E1D0 60%, #E8F2E5 100%)",
  secondary: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  secondaryHover: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
};

// Create the theme with optimizations
const theme = createTheme({
  direction: "rtl",
  // Optimize for production
  unstable_strictMode: true,
  palette: {
    mode: "light",
    primary: {
      main: "#797397",
      light: "#D1EBDD",
      dark: "#F3E3EE",
      contrastText: "#2c3e50",
    },
    secondary: {
      main: "#667eea",
      light: "#764ba2",
      dark: "#5a6fd8",
      contrastText: "#ffffff",
    },
    background: {
      default: "#F3F8F0",
      paper: "#ffffff",
    },
    text: {
      primary: "#2c3e50",
      secondary: "#5a6c7d",
    },
  },
  typography: {
    fontFamily:  '"Heebo", "Rubik", "Rubik Hebrew", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "3.5rem",
      fontWeight: 700,
      lineHeight: 1.2,
      color: "#2c3e50",
    },
    h2: {
      fontSize: "2.5rem",
      fontWeight: 600,
      lineHeight: 1.3,
      color: "#2c3e50",
    },
    h3: {
      fontSize: "2rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#2c3e50",
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 500,
      lineHeight: 1.4,
      color: "#2c3e50",
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 500,
      lineHeight: 1.5,
      color: "#2c3e50",
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.6,
      color: "#2c3e50",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
      color: "#2c3e50",
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.6,
      color: "#5a6c7d",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily:
            '"Heebo", "Rubik", "Rubik Hebrew", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "25px",
          textTransform: "none",
          fontSize: "14px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "none",
        },
        contained: {
          background: gradientColors.primary,
          color: "#2c3e50",
          border: "none",
          "&:hover": {
            background: gradientColors.primaryHover,
            boxShadow: "none",
            transform: "translateY(-2px)",
          },
        },
        outlined: {
          border: "1px solid #797397",
          color: "#797397",
        },
      },
    },
  },
  shape: {
    borderRadius: 25,
  },
  spacing: 8,
});

// Export gradient colors for use in components
export { gradientColors };
export default theme;

// Editor design tokens via CSS variables (initialized in browser)
if (typeof document !== "undefined") {
  const root = document.documentElement;
  root.style.setProperty("--editor-accent-1", "#6EE7F9");
  root.style.setProperty("--editor-accent-2", "#A78BFA");
  root.style.setProperty("--editor-radius-lg", "16px");
  root.style.setProperty("--editor-shadow-1", "0 10px 30px rgba(0,0,0,0.10)");
  root.style.setProperty("--editor-shadow-2", "0 14px 34px rgba(0,0,0,0.16)");
}
